/**
 * Error tracking setup for production
 * Can be extended with Sentry or other error tracking services
 */

import config from '../config';

interface ErrorInfo {
  message: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  userAgent: string;
  url: string;
  environment: string;
}

class ErrorTracker {
  private isEnabled: boolean;

  constructor() {
    this.isEnabled = config.ENABLE_ERROR_TRACKING && config.ENVIRONMENT === 'production';
  }

  /**
   * Log error to console and potentially to external service
   */
  logError(error: Error, errorInfo?: any) {
    if (!this.isEnabled) {
      // In development, just log to console
      console.error('Error tracked:', error, errorInfo);
      return;
    }

    const errorData: ErrorInfo = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      environment: config.ENVIRONMENT,
    };

    // Log to console in production
    console.error('Production error:', errorData);

    // TODO: Send to error tracking service (Sentry, etc.)
    // Example with Sentry:
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     contexts: { errorInfo: errorData }
    //   });
    // }

    // Fallback: Send to your own endpoint
    this.sendToBackend(errorData);
  }

  /**
   * Send error to backend for logging
   */
  private async sendToBackend(errorData: ErrorInfo) {
    try {
      // Only send critical errors to avoid overwhelming the backend
      if (this.isCriticalError(errorData.message)) {
        await fetch(`${config.API_URL}/api/errors`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(errorData),
        });
      }
    } catch (err) {
      // Silently fail - don't want error reporting to cause more errors
      console.error('Failed to send error to backend:', err);
    }
  }

  /**
   * Determine if an error is critical and should be reported
   */
  private isCriticalError(message: string): boolean {
    const criticalPatterns = [
      /authentication/i,
      /unauthorized/i,
      /network/i,
      /api/i,
      /database/i,
      /payment/i,
    ];

    return criticalPatterns.some(pattern => pattern.test(message));
  }

  /**
   * Log a warning (non-fatal issue)
   */
  logWarning(message: string, context?: any) {
    if (config.ENVIRONMENT === 'development') {
      console.warn('Warning:', message, context);
    }
  }

  /**
   * Log an info message (for tracking important events)
   */
  logInfo(message: string, context?: any) {
    if (config.ENVIRONMENT === 'development') {
      console.info('Info:', message, context);
    }
  }
}

export const errorTracker = new ErrorTracker();
export default errorTracker;